"use client";

import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react";
import type { ReactNode } from "react";

type Direction = "up" | "down" | "left" | "right" | "none";

interface RevealProps extends Omit<HTMLMotionProps<"div">, "initial" | "animate" | "whileInView"> {
  /** Direction the element travels from on enter. Default: "up". */
  direction?: Direction;
  /** Pixel distance to travel. Default: 24. */
  distance?: number;
  /** Stagger delay in seconds. Default: 0. */
  delay?: number;
  /** Animation duration in seconds. Default: 0.6. */
  duration?: number;
  /** Viewport amount required to trigger (0–1). Default: 0.15. */
  amount?: number;
  /** Replay every time it enters viewport. Default: false (one-shot). */
  repeat?: boolean;
  children: ReactNode;
}

const OFFSET: Record<Direction, { x: number; y: number }> = {
  up:    { x: 0,  y: 1  },
  down:  { x: 0,  y: -1 },
  left:  { x: 1,  y: 0  },
  right: { x: -1, y: 0  },
  none:  { x: 0,  y: 0  },
};

/**
 * Standard scroll-reveal wrapper. Fades + translates in when scrolled into view.
 * Respects `prefers-reduced-motion` automatically (renders content statically).
 *
 * Use sparingly — one Reveal per logical block (heading, card, paragraph group),
 * not per word. Stagger groups with `delay`, never with nested Reveals.
 */
export default function Reveal({
  direction = "up",
  distance = 24,
  delay = 0,
  duration = 0.6,
  amount = 0.15,
  repeat = false,
  children,
  ...rest
}: RevealProps) {
  const reduced = useReducedMotion();
  const offset = OFFSET[direction];

  if (reduced) {
    return <div {...(rest as object)}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: offset.x * distance, y: offset.y * distance }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: !repeat, amount }}
      transition={{
        duration,
        delay,
        ease: [0.23, 1, 0.32, 1], // matches --ease-out
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
